import type {MouseEvent, ReactElement} from 'react';
import {useCallback, useContext} from 'react';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {useIsEmptyTextComponent} from '@EmailMakerReact/hooks/use-is-empty-text-component';
import {replaceEmptyTextWithDivider} from '@EmailMakerReact/lib/email-maker-design-library';
import type {EmailMakerControlProps} from '../../right-panel-registry.types';
import {COLLAPSIBLE_CONTROL_SET} from '../info-box-control/info-box-control';
import {CollapsibleInfoBox} from '../info-box-control/components/collapsible-info-box/collapsible-info-box';
import {InfoBox} from '../info-box-control/components/info-box/info-box';
import {CLICK_EVENT_TYPE, EMPTY_TEXT_WARNING_DESCRIPTOR, REPLACE_WITH_DIVIDER_CLASS_NAME} from './empty-text-warning-control.types';

export function EmptyTextWarningControl({component}: EmailMakerControlProps): ReactElement {
  const isEmpty = useIsEmptyTextComponent(component);
  const {editor} = useContext(emailMakerContext);

  const onClickReplaceWithDivider = useCallback(
    (event: MouseEvent<HTMLDivElement>): void => {
      if (!isReplaceWithDividerClick(event)) {
        return;
      }

      event.preventDefault();
      if (editor) {
        replaceEmptyTextWithDivider(editor, component);
      }
    },
    [editor, component]
  );

  return (
    <CollapsibleInfoBox isVisible={isEmpty} spacingClassName="spacing-m-b-4">
      <ClickableDiv onClick={onClickReplaceWithDivider} role="presentation" tabIndex={-1}>
        <InfoBox descriptor={EMPTY_TEXT_WARNING_DESCRIPTOR} />
      </ClickableDiv>
    </CollapsibleInfoBox>
  );
}

function isReplaceWithDividerClick(event: MouseEvent<HTMLDivElement>): boolean {
  const {target} = event;
  return event.type === CLICK_EVENT_TYPE && target instanceof HTMLElement && target.closest(`.${REPLACE_WITH_DIVIDER_CLASS_NAME}`) !== null;
}

COLLAPSIBLE_CONTROL_SET.add(EmptyTextWarningControl);
